import React, { useState } from "react";
import type { ReactNode } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { StepRenderProps } from "@/components/onboarding/components/welcome-wizard";
import {
  FooterContainer,
  FooterSkipButton,
  StepContent,
  StepTitle,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { navigateToExternalWindowUrl } from "@/modules/window/navigation";
import { APP_CONFIG } from "@/domains/global/config";
import {
  MDSButton,
  MDSButtonSize,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSIcon,
  MDSIconColor,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { clientEnvModule } from "@/modules/client-env";
import { decorateMarketingAttributionUrl } from "@/app/router/marketing-attribution";
import { useAppStore } from "@/store";

import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { logger } from "@/modules/logger";
import {
  AppDownloadButton,
  AppIconWrapper,
} from "@/components/onboarding/components/app-download-button";
import type { OnboardingIntent } from "@/components/proactive-trial/intent-fork";
import { IntentVignette } from "@/components/proactive-trial/intent-fork";

const NATIVE_APP_DOWNLOAD_DESCRIPTION =
  "Capture thoughts the moment they strike—talk, type, snap it, share it. And with Mem Proactive, you'll get reminded right when it matters.";

const IOS_APP_DOWNLOAD_DESCRIPTION =
  "Capture thoughts the moment they strike and access your notes offline. And with Mem Proactive, you'll get reminded right when it matters.";

const INTENT_DOWNLOAD_BODY: Record<OnboardingIntent, { mac: string; computer: string }> = {
  meetings: {
    mac: "Let Mem take your meeting notes — get notified when meetings start, join in one click, and get clean notes plus follow-ups on the things you said you'd do.",
    computer:
      "Let Mem take your meeting notes — get notified when meetings start, join in one click, and get clean notes plus follow-ups on the things you said you'd do.",
  },
  quick_thoughts: {
    mac: "Mem lives on your computer so it's just one keypress away when a thought hits. Capture it once, forget it — Mem follows up until it's handled.",
    computer:
      "Mem lives on your computer so it's just one keypress away when a thought hits. Capture it once, forget it — Mem follows up until it's handled.",
  },
};

export type NativeAppDownloadViewState = "post_click" | "pre_click";

const getIntentDownloadCopy = ({
  appName,
  intent,
}: {
  appName: string;
  intent: OnboardingIntent;
}) => {
  if (appName === "Mac app") {
    return {
      body: INTENT_DOWNLOAD_BODY[intent].mac,
      buttonLabel: "Download Mem for Mac",
      title: "Get Mem on your Mac",
    };
  }

  if (appName === "iOS app") {
    return {
      body:
        intent === "meetings"
          ? "Mem works from your computer so it can capture your meetings as they happen — and bring back whatever's left unfinished."
          : "Mem lives on your computer so it's one keypress away when a thought hits. Say it, forget it — Mem follows up until it's handled.",
      buttonLabel: "Download Mem",
      title: "Get the Mem app",
    };
  }

  if (appName === "Windows app") {
    return {
      body: INTENT_DOWNLOAD_BODY[intent].computer,
      buttonLabel: "Download Mem for Windows",
      title: "Get Mem on Windows",
    };
  }

  return {
    body: INTENT_DOWNLOAD_BODY[intent].computer,
    buttonLabel: "Download Mem",
    title: "Get the Mem app",
  };
};

const getOsConfig = ({
  isMacBrowser,
  isWindowsBrowser,
  isIosBrowser,
}: {
  isMacBrowser: boolean;
  isWindowsBrowser: boolean;
  isIosBrowser: boolean;
}): {
  appName: string | null;
  blockingTitle: string | null;
  buttonLabel: string | null;
  description: string | null;
  downloadUrl: string | null;
} => {
  if (isIosBrowser) {
    return {
      appName: "iOS app",
      blockingTitle: "Download the iOS app to continue",
      buttonLabel: "Get iOS app",
      description: IOS_APP_DOWNLOAD_DESCRIPTION,
      downloadUrl: APP_CONFIG.URLS.IOS_APP_ONBOARDING,
    };
  }

  if (isMacBrowser) {
    return {
      appName: "Mac app",
      blockingTitle: "Download the Mac app to continue",
      buttonLabel: "Get Mac app",
      description: NATIVE_APP_DOWNLOAD_DESCRIPTION,
      downloadUrl: APP_CONFIG.URLS.MAC_APP_ONBOARDING,
    };
  }

  if (isWindowsBrowser) {
    return {
      appName: "Windows app",
      blockingTitle: "Download the Windows app to continue",
      buttonLabel: "Get Windows app",
      description: NATIVE_APP_DOWNLOAD_DESCRIPTION,
      downloadUrl: APP_CONFIG.URLS.WINDOWS_APP_ONBOARDING,
    };
  }

  return {
    appName: null,
    blockingTitle: null,
    buttonLabel: null,
    description: null,
    downloadUrl: null,
  };
};

interface NativeAppDownloadStepViewProps {
  appName: string;
  blockingTitle: string;
  buttonLabel: string;
  description: string;
  iconKind: MDSIconKind;
  initialDownloadState?: NativeAppDownloadViewState;
  intent?: OnboardingIntent | null;
  isApple: boolean;
  isBlockingNewUserOnboarding: boolean;
  onDownload: () => void;
  onSkip: () => void;
  postClickAction?: ReactNode;
  signedInEmail?: string | null;
  signInProviderName?: string;
}

/**
 * Renders the platform download handoff while allowing the preceding intent
 * choice to explain why moving into the native app is worth the interruption.
 */
export const NativeAppDownloadStepView = ({
  appName,
  blockingTitle,
  buttonLabel,
  description,
  iconKind,
  initialDownloadState = "pre_click",
  intent = null,
  isApple,
  isBlockingNewUserOnboarding,
  onDownload,
  onSkip,
  postClickAction,
  signedInEmail = null,
  signInProviderName = "Google",
}: NativeAppDownloadStepViewProps) => {
  const [downloadState, setDownloadState] =
    useState<NativeAppDownloadViewState>(initialDownloadState);
  const shouldReduceMotion = useReducedMotion() === true;

  if (isBlockingNewUserOnboarding && intent !== null) {
    const intentCopy = getIntentDownloadCopy({ appName, intent });
    const transitionDuration = shouldReduceMotion ? 0 : 0.4;

    const handleIntentDownload = () => {
      setDownloadState("post_click");
      onDownload();
    };

    return (
      <WizardContentWrapper aria-live="polite">
        <AnimatePresence initial={false} mode="wait">
          {downloadState === "pre_click" ? (
            <DownloadStatePanel
              key="pre-click"
              initial={{ opacity: 1, y: 0 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: shouldReduceMotion ? 0 : -18 }}
              transition={{ duration: transitionDuration, ease: "easeInOut" }}
            >
              <StepTitle>{intentCopy.title}</StepTitle>
              <StepContent>
                <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
                  {intentCopy.body}
                </MDSText>

                <DownloadVignette>
                  <IntentVignette intent={intent} />
                </DownloadVignette>

                <IntentDownloadButton
                  size={MDSButtonSize.Large}
                  variant={MDSButtonVariant.Contrast}
                  onPress={handleIntentDownload}
                >
                  <MDSIcon
                    slot={MDSButtonSlotName.LeftIcon}
                    kind={iconKind}
                    color={MDSIconColor.OnAccent}
                    size={16}
                  />
                  {intentCopy.buttonLabel}
                </IntentDownloadButton>
              </StepContent>
            </DownloadStatePanel>
          ) : (
            <DownloadStatePanel
              key="post-click"
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: shouldReduceMotion ? 0 : 0.24,
                ease: "easeOut",
              }}
            >
              <StepTitle>While that downloads…</StepTitle>
              <StepContent>
                <MDSText variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Secondary}>
                  Three steps and Mem picks up right where you left off.
                </MDSText>

                <HandoffSteps>
                  <HandoffStep
                    initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: shouldReduceMotion ? 0 : 0.22 }}
                  >
                    <StepNumber>1</StepNumber>
                    <MDSText variant={MDSTextVariant.ParagraphSm}>
                      Open the downloaded file to install Mem
                    </MDSText>
                  </HandoffStep>
                  <HandoffStep
                    initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: shouldReduceMotion ? 0 : 0.22,
                      duration: shouldReduceMotion ? 0 : 0.22,
                    }}
                  >
                    <StepNumber>2</StepNumber>
                    <MDSText variant={MDSTextVariant.ParagraphSm}>
                      Open Mem and sign in {signedInEmail ? "as " : "with the same "}
                      {signedInEmail ? (
                        <AccountIdentity>{signedInEmail}</AccountIdentity>
                      ) : (
                        <AccountIdentity>{signInProviderName} account</AccountIdentity>
                      )}
                    </MDSText>
                  </HandoffStep>
                  <HandoffStep
                    initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      delay: shouldReduceMotion ? 0 : 0.44,
                      duration: shouldReduceMotion ? 0 : 0.22,
                    }}
                  >
                    <StepNumber>3</StepNumber>
                    <MDSText variant={MDSTextVariant.ParagraphSm}>
                      You&apos;ll continue right where you left off
                    </MDSText>
                  </HandoffStep>
                </HandoffSteps>

                {postClickAction}

                <DownloadAgain>
                  Didn&apos;t start?{" "}
                  <DownloadAgainButton type="button" onClick={onDownload}>
                    Download again
                  </DownloadAgainButton>
                </DownloadAgain>
              </StepContent>
            </DownloadStatePanel>
          )}
        </AnimatePresence>
      </WizardContentWrapper>
    );
  }

  return (
    <WizardContentWrapper>
      <StepTitle>
        {isBlockingNewUserOnboarding ? blockingTitle : `Mem is better on the ${appName}`}
      </StepTitle>

      <StepContent>
        <DownloadReason>
          <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
            {description}
          </MDSText>
        </DownloadReason>

        <AppDownloadButton onClick={onDownload}>
          <AppIconWrapper data-icon-wrapper isApple={isApple}>
            <MDSIcon kind={iconKind} size={16} />
          </AppIconWrapper>
          <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
            {isBlockingNewUserOnboarding ? buttonLabel : `Download ${appName}`}
          </MDSText>
        </AppDownloadButton>
      </StepContent>

      {isBlockingNewUserOnboarding ? null : (
        <FooterContainer align="flex-end">
          <FooterSkipButton onClick={onSkip}>Skip for now</FooterSkipButton>
        </FooterContainer>
      )}
    </WizardContentWrapper>
  );
};

export const NativeAppDownloadStep: React.FC<StepRenderProps> = observer(({ onComplete }) => {
  const { store } = useAppStore();
  const isMacBrowser = clientEnvModule.isMacBrowser();
  const isWindowsBrowser = clientEnvModule.isWindowsBrowser();
  const isIosBrowser = clientEnvModule.isIosBrowser();
  const isBlockingNewUserOnboarding =
    store.onboarding.context.onboardingFlowVersion === "intent_v1" ||
    store.onboarding.context.hasPendingNativeAppDownloadBlockingOnboarding;

  const { appName, blockingTitle, buttonLabel, description, downloadUrl } = getOsConfig({
    isMacBrowser,
    isWindowsBrowser,
    isIosBrowser,
  });

  const handleDownload = async () => {
    if (!downloadUrl) {
      logger.error({
        message: "Download URL is null in NativeAppDownloadStep",
        info: {
          appName,
          downloadUrl,
        },
      });

      return;
    }

    trackEvent(TrackedEvent.NativeUpsellDownload, {
      platform: appName,
      onboarding_flow_version: store.onboarding.context.onboardingFlowVersion ?? undefined,
      onboarding_intent: store.onboarding.context.onboardingIntent ?? undefined,
    });

    if (!isBlockingNewUserOnboarding) {
      onComplete();
    }

    await navigateToExternalWindowUrl({
      url: decorateMarketingAttributionUrl({
        url: new URL(downloadUrl),
      }),
    });
  };

  const handleSkip = (untracked: boolean = false) => {
    if (!untracked) {
      trackEvent(TrackedEvent.NativeUpsellSkip, {
        platform: appName,
      });
    }

    onComplete();
  };

  useEffectOnMount(() => {
    trackEvent(TrackedEvent.NativeUpsellView, {
      platform: appName,
      onboarding_flow_version: store.onboarding.context.onboardingFlowVersion ?? undefined,
      onboarding_intent: store.onboarding.context.onboardingIntent ?? undefined,
    });
  });

  useEffectOnMount(() => {
    if (!downloadUrl || !appName || !blockingTitle || !buttonLabel || !description) {
      // this would be highly exceptional
      logger.error({
        message:
          "Download URL, app name, blocking title, button label, or description is null in NativeAppDownloadStep",
        info: {
          downloadUrl,
          appName,
          blockingTitle,
          buttonLabel,
          description,
        },
      });

      if (!isBlockingNewUserOnboarding) {
        handleSkip(true);
      }
    }
  });

  if (!downloadUrl || !appName || !blockingTitle || !buttonLabel || !description) {
    return null;
  }

  const iconKind = isWindowsBrowser ? MDSIconKind.BrandWindows : MDSIconKind.BrandApple;

  return (
    <NativeAppDownloadStepView
      appName={appName}
      blockingTitle={blockingTitle}
      buttonLabel={buttonLabel}
      description={description}
      iconKind={iconKind}
      isApple={isMacBrowser || isIosBrowser}
      isBlockingNewUserOnboarding={isBlockingNewUserOnboarding}
      intent={store.platformInfo.onboardingInfo.onboardingIntent ?? undefined}
      onDownload={handleDownload}
      onSkip={() => handleSkip()}
    />
  );
});

const DownloadReason = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const DownloadStatePanel = styled(motion.div)`
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
`;

const DownloadVignette = styled.div`
  width: min(100%, 280px);
  align-self: center;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};

  [data-intent-vignette] {
    border-bottom: 0;
  }
`;

const IntentDownloadButton = styled(MDSButton)`
  width: 100%;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const HandoffSteps = styled.ol`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  margin: 0;
  padding: 0;
  list-style: none;
`;

const HandoffStep = styled(motion.li)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[4]};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  background: ${({ theme }) => theme.colors.surface.modal};
`;

const StepNumber = styled.span`
  display: flex;
  width: 30px;
  height: 30px;
  flex: none;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.max};
  background: ${({ theme }) => theme.colors.background.button.emphasisLight.default};
  color: ${({ theme }) => theme.colors.text.emphasis};
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
`;

const AccountIdentity = styled.strong`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
`;

const DownloadAgain = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  line-height: ${({ theme }) => theme.lineHeights["text-s"]};
  text-align: center;
`;

const DownloadAgainButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;

  &:focus-visible {
    border-radius: 2px;
    outline: 2px solid ${({ theme }) => theme.colors.border.emphasis};
    outline-offset: 3px;
  }
`;
